/**
 * MomentsBrisbane Brand Configuration
 *
 * Private Chef at Home - Hamish Miller
 * Domain: momentsbrisbane.com
 * Tenant ID: 699d4b9e92752d4ca2080bb4
 */

export interface BrandConfig {
  siteName: string;
  tagline: string;
  description: string;
  tenantId: string;
  apiUrl?: string;
  brandIdentifier: string;
  logo: {
    src: string;
    alt: string;
    favicon: string;
  };
  contact: {
    businessType: string;
    industry: string;
    email?: string;
    phone?: string;
    address?: string;
    contactName?: string;
    businessName?: string;
  };
  colors: {
    primary: string;
    primaryHover: string;
    secondary: string;
    secondaryHover: string;
    accent: string;
    accentLight: string;
    background: {
      main: string;
      light: string;
      card: string;
      overlay: string;
      surface: string;
      darkSurface?: string;
      cardGradient?: string;
      darkOverlay?: string;
    };
    status: {
      success: string;
      warning: string;
      error: string;
      info: string;
    };
    text: {
      primary: string;
      secondary: string;
      muted: string;
      inverse: string;
      primaryDark?: string;
      secondaryDark?: string;
      mutedDark?: string;
    };
    border: {
      light: string;
      medium: string;
      dark: string;
      darkCard?: string;
    };
    [key: string]: any;
  };
  fonts: {
    heading: string;
    body: string;
  };
  components: {
    button: {
      primaryBg: string;
      primaryHover: string;
      secondaryBg: string;
      secondaryHover: string;
      [key: string]: any;
    };
    tooltip?: {
      bg: string;
      color: string;
      [key: string]: any;
    };
    menu?: {
      bg: string;
      itemBg: string;
      itemColor: string;
      itemHoverBg: string;
      itemHoverColor: string;
      borderColor: string;
    };
    card: {
      bg: string;
      border: string;
      shadow: string;
      [key: string]: any;
    };
    navbar: {
      bg: string;
      text: string;
      shadow: string;
    };
    footer: {
      bg: string;
      text: string;
      linkColor: string;
      linkHover: string;
    };
    form: {
      bg: string;
      fieldBg: string;
      fieldBorder: string;
      fieldBorderFocus: string;
      fieldShadow: string;
      fieldShadowFocus: string;
      labelColor: string;
      placeholderColor: string;
    };
  };
  membership: {
    totalLegacyMemberships: number;
    memberBenefits: {
      discount: string;
      features: string[];
    };
  };
  ipfs?: {
    gatewayUrl: string;
  };
}

export const brandConfig: BrandConfig = {
  siteName: "Moments Brisbane",
  tagline: "Private Chef at Home",
  description: "Michelin-trained private chef Hamish Miller brings fine dining to your home in Brisbane. Bespoke menus, dietary accommodations, unforgettable evenings.",

  tenantId: "699d4b9e92752d4ca2080bb4",
  brandIdentifier: "MomentsBrisbane",

  logo: {
    src: "/favicon.svg",
    alt: "Moments Brisbane",
    favicon: "/favicon.svg"
  },

  contact: {
    businessType: "Private Chef Services",
    industry: "Hospitality",
    email: "hamish@momentsbrisbane.com",
    phone: "0451 432 603",
    address: "Brisbane, Queensland",
    contactName: "Hamish Miller",
    businessName: "Moments Brisbane"
  },

  colors: {
    primary: "#c8a45e",
    primaryHover: "#dfc07f",
    secondary: "#090807",
    secondaryHover: "#161412",

    accent: "#a8843e",
    accentLight: "rgba(200,164,94,0.08)",

    background: {
      main: "#090807",
      light: "#0f0e0c",
      card: "#121110",
      overlay: "rgba(200,164,94,0.05)",
      surface: "#161412",
      darkSurface: "#090807",
      cardGradient: "linear-gradient(135deg, rgba(18,17,16,0.8) 0%, rgba(22,20,18,0.6) 50%, rgba(18,17,16,0.8) 100%)",
      darkOverlay: "rgba(0,0,0,0.2)"
    },

    status: {
      success: "#30D158",
      warning: "#FF9F0A",
      error: "#FF3B30",
      info: "#c8a45e"
    },

    text: {
      primary: "#f5f0e8",
      secondary: "#a09a8e",
      muted: "#6b6560",
      inverse: "#090807",
      primaryDark: "#f5f0e8",
      secondaryDark: "#a09a8e",
      mutedDark: "#6b6560"
    },

    border: {
      light: "#231f1b",
      medium: "rgba(255,255,255,0.06)",
      dark: "rgba(255,255,255,0.1)",
      darkCard: "rgba(255,255,255,0.06)"
    }
  },

  fonts: {
    heading: "'Cormorant Garamond', Georgia, 'Times New Roman', serif",
    body: "'Outfit', system-ui, -apple-system, sans-serif"
  },

  components: {
    button: {
      primaryBg: "#c8a45e",
      primaryHover: "#dfc07f",
      secondaryBg: "rgba(200,164,94,0.1)",
      secondaryHover: "rgba(200,164,94,0.2)"
    },
    tooltip: {
      bg: "#c8a45e",
      color: "#090807"
    },
    menu: {
      bg: "rgba(18,17,16,0.98)",
      itemBg: "transparent",
      itemColor: "#f5f0e8",
      itemHoverBg: "rgba(200,164,94,0.15)",
      itemHoverColor: "#dfc07f",
      borderColor: "#231f1b"
    },
    card: {
      bg: "#121110",
      border: "#231f1b",
      shadow: "0 1px 3px rgba(0,0,0,0.3)",
      darkShadow: "0 8px 32px rgba(0,0,0,0.4)",
      hoverShadow: "0 10px 30px rgba(0,0,0,0.4)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "#090807",
      text: "#f5f0e8",
      shadow: "0 1px 3px rgba(0,0,0,0.3)"
    },
    footer: {
      bg: "#090807",
      text: "#a09a8e",
      linkColor: "#c8a45e",
      linkHover: "#dfc07f"
    },
    form: {
      bg: "#121110",
      fieldBg: "rgba(22,20,18,0.8)",
      fieldBorder: "#231f1b",
      fieldBorderFocus: "#c8a45e",
      fieldShadow: "0 1px 2px rgba(0,0,0,0.3)",
      fieldShadowFocus: "0 0 0 3px rgba(200,164,94,0.15), 0 1px 2px rgba(0,0,0,0.3)",
      labelColor: "#f5f0e8",
      placeholderColor: "#6b6560"
    }
  },

  membership: {
    totalLegacyMemberships: 0,
    memberBenefits: {
      discount: "VIP dining",
      features: [
        "Priority Booking",
        "Custom Menu Design",
        "Wine Pairing Consultation"
      ]
    }
  },

  ipfs: {
    gatewayUrl: 'https://files.tommillerservices.com/ipfs'
  }
};

export const lightTheme = {
  primary: "#c8a45e",
  primaryHover: "#dfc07f",
  secondary: "#090807",
  secondaryHover: "#161412",
  accent: "#a8843e",
  accentLight: "rgba(200,164,94,0.08)",
  background: {
    main: "#FFFFFF",
    light: "#FAF8F5",
    card: "#FFFFFF",
    overlay: "rgba(200,164,94,0.05)",
    surface: "#F5F3EF",
    cardGradient: "linear-gradient(135deg, #FFFFFF 0%, #FAF8F5 100%)",
    darkOverlay: "rgba(0,0,0,0.1)",
  },
  text: {
    primary: "#1D1D1F",
    secondary: "#6E6E73",
    muted: "#8E8E93",
    inverse: "#FFFFFF",
    primaryDark: "#1D1D1F",
    secondaryDark: "#6E6E73",
    mutedDark: "#8E8E93"
  },
  border: {
    light: "#E2E0DB",
    medium: "#CBD5E0",
    dark: "#A0AEC0",
    darkCard: "#E2E0DB"
  },
  components: {
    card: {
      bg: "#FFFFFF",
      border: "#E2E0DB",
      shadow: "0 1px 3px rgba(0,0,0,0.12)",
      darkShadow: "0 1px 3px rgba(0,0,0,0.12)",
      hoverShadow: "0 10px 30px rgba(0,0,0,0.15)",
      backdropFilter: "none"
    },
    navbar: {
      bg: "#FFFFFF",
      text: "#1D1D1F",
      shadow: "0 1px 3px rgba(0,0,0,0.1)"
    },
    footer: {
      bg: "#FAF8F5",
      text: "#6E6E73",
      linkColor: "#c8a45e",
      linkHover: "#a8843e"
    },
    button: {
      primaryBg: "#c8a45e",
      primaryHover: "#a8843e",
      secondaryBg: "rgba(200,164,94,0.1)",
      secondaryHover: "rgba(200,164,94,0.2)"
    }
  }
};

export const darkTheme = {
  primary: "#c8a45e",
  primaryHover: "#dfc07f",
  secondary: "#090807",
  secondaryHover: "#161412",
  accent: "#a8843e",
  accentLight: "rgba(200,164,94,0.08)",
  background: {
    main: "#090807",
    light: "#0f0e0c",
    card: "#121110",
    overlay: "rgba(200,164,94,0.05)",
    surface: "#161412",
    darkSurface: "#090807",
    cardGradient: "linear-gradient(135deg, rgba(18,17,16,0.8) 0%, rgba(22,20,18,0.6) 50%, rgba(18,17,16,0.8) 100%)",
    darkOverlay: "rgba(0,0,0,0.2)",
  },
  text: {
    primary: "#f5f0e8",
    secondary: "#a09a8e",
    muted: "#6b6560",
    inverse: "#090807",
    primaryDark: "#f5f0e8",
    secondaryDark: "#a09a8e",
    mutedDark: "#6b6560"
  },
  border: {
    light: "#231f1b",
    medium: "rgba(255,255,255,0.06)",
    dark: "rgba(255,255,255,0.1)",
    darkCard: "rgba(255,255,255,0.06)"
  },
  components: {
    card: {
      bg: "#121110",
      border: "#231f1b",
      shadow: "0 1px 3px rgba(0,0,0,0.3)",
      darkShadow: "0 8px 32px rgba(0,0,0,0.4)",
      hoverShadow: "0 10px 30px rgba(0,0,0,0.4)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "#090807",
      text: "#f5f0e8",
      shadow: "0 1px 3px rgba(0,0,0,0.3)"
    },
    footer: {
      bg: "#090807",
      text: "#a09a8e",
      linkColor: "#c8a45e",
      linkHover: "#dfc07f"
    },
    button: {
      primaryBg: "#c8a45e",
      primaryHover: "#dfc07f",
      secondaryBg: "rgba(200,164,94,0.1)",
      secondaryHover: "rgba(200,164,94,0.2)"
    },
    form: {
      bg: "#121110",
      fieldBg: "rgba(22,20,18,0.8)",
      fieldBorder: "#231f1b",
      fieldBorderFocus: "#c8a45e",
      fieldShadow: "0 1px 2px rgba(0,0,0,0.3)",
      fieldShadowFocus: "0 0 0 3px rgba(200,164,94,0.15), 0 1px 2px rgba(0,0,0,0.3)",
      labelColor: "#f5f0e8"
    }
  }
};

export const getColor = (path: string, colorMode?: 'light' | 'dark'): string => {
  const mode = colorMode || (localStorage.getItem('chakra-ui-color-mode') as 'light' | 'dark') || 'dark';
  const theme = mode === 'light' ? lightTheme : darkTheme;

  const keys = path.split(".");

  if (keys[0] === "components") {
    let value: any = theme;
    for (const key of keys) {
      value = value[key];
      if (value === undefined) {
        let fallback: any = brandConfig.colors;
        const colorKeys = keys.slice(1);
        for (const key of colorKeys) {
          fallback = fallback[key];
          if (fallback === undefined) break;
        }
        if (fallback !== undefined) return fallback;
        console.warn(`Color path "${path}" not found in theme config for mode: ${mode}`);
        return "#c8a45e";
      }
    }
    return value;
  }

  let value: any = theme;
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      let fallback: any = brandConfig.colors;
      for (const key of keys) {
        fallback = fallback[key];
        if (fallback === undefined) break;
      }
      if (fallback !== undefined) return fallback;
      console.warn(`Color path "${path}" not found in theme config for mode: ${mode}`);
      return "#c8a45e";
    }
  }

  return value;
};

export const getBrandValue = (path: string): any => {
  const keys = path.split(".");
  let value: any = brandConfig;
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Brand config path "${path}" not found`);
      return null;
    }
  }
  return value;
};

export const getComponent = (component: string, property: string, colorMode?: 'light' | 'dark'): string => {
  const mode = colorMode || (localStorage.getItem('chakra-ui-color-mode') as 'light' | 'dark') || 'dark';
  const theme = mode === 'light' ? lightTheme : darkTheme;

  const componentConfig = (theme.components as any)?.[component];
  if (!componentConfig) {
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const value = fallbackConfig[property];
      if (value) return value;
    }
    console.warn(`Component "${component}" not found in theme config for mode: ${mode}`);
    return "#c8a45e";
  }

  const value = componentConfig[property];
  if (!value) {
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const fallbackValue = fallbackConfig[property];
      if (fallbackValue) return fallbackValue;
    }
    console.warn(`Property "${property}" not found for component "${component}" in mode: ${mode}`);
    return "#c8a45e";
  }

  return value;
};

export const generateCSSCustomProperties = (): string => {
  const { colors } = brandConfig;
  return `
    :root {
      --brand-primary: ${colors.primary};
      --brand-primary-hover: ${colors.primaryHover};
      --brand-secondary: ${colors.secondary};
      --brand-secondary-hover: ${colors.secondaryHover};
      --brand-accent: ${colors.accent};
      --brand-accent-light: ${colors.accentLight};

      --brand-bg-main: ${colors.background.main};
      --brand-bg-light: ${colors.background.light};
      --brand-bg-card: ${colors.background.card};
      --brand-bg-overlay: ${colors.background.overlay};
      --brand-bg-surface: ${colors.background.surface};

      --brand-success: ${colors.status.success};
      --brand-warning: ${colors.status.warning};
      --brand-error: ${colors.status.error};
      --brand-info: ${colors.status.info};

      --brand-text-primary: ${colors.text.primary};
      --brand-text-secondary: ${colors.text.secondary};
      --brand-text-muted: ${colors.text.muted};
      --brand-text-inverse: ${colors.text.inverse};

      --brand-border-light: ${colors.border.light};
      --brand-border-medium: ${colors.border.medium};
      --brand-border-dark: ${colors.border.dark};

      --brand-font-heading: ${brandConfig.fonts.heading};
      --brand-font-body: ${brandConfig.fonts.body};
    }
  `;
};

export default brandConfig;
